import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Form, Button, FormControl, InputGroup, Modal, Table, Row, Col } from 'react-bootstrap';
import { FaTimes, FaCheck, FaPlus } from 'react-icons/fa';
import { getAllDiscounts, getDiscountData } from '../../services/Discounts/discountsService';
import PaginationComponent from '../Pagination/PaginationComponent';
import CreateDiscountModal from '../Discounts/CreateDiscountModal';

const DiscountInput = ({ value, onChange, label, isInvalid, userRoles, createdNew, readOnly, product }) => {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [searchText, setSearchText] = useState(value?.description || '');
  const [isReadOnly, setIsReadOnly] = useState(false);
  const [discounts, setDiscounts] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [filterParams, setFilterParams] = useState({});
  const previousFilterParams = useRef({});
  const prevValueRef = useRef(null);

  // eslint-disable-next-line
  const [selectedDiscount, setSelectedDiscount] = useState(value || null);

  const handleSelectDiscount = useCallback(async (discount) => {
    if (!discount || !discount.id) return;

    try {
      const data = await getDiscountData(discount.id);
      setSelectedDiscount(discount);
      setSearchText(discount.description);
      setIsReadOnly(true);
      onChange({ id: discount.id, name: discount.description, discount: data });
      setShowModal(false);
    } catch (error) {}
  }, [onChange]);

  useEffect(() => {
    if (value && value.id && prevValueRef.current?.id !== value.id) {
      prevValueRef.current = value;
      handleSelectDiscount(value);
    } else if (!value) {
      setSelectedDiscount(null);
      setSearchText('');
      setIsReadOnly(false);
    }
  }, [value, handleSelectDiscount]);

  const fetchDiscounts = useCallback(async (filters = filterParams, currentPage = page, currentPageSize = pageSize) => {
    try {
      const data = await getAllDiscounts(filters, currentPage - 1, currentPageSize);
      setDiscounts(data.content);
      setTotalPages(data.totalPages);
    } catch (error) {}
  }, [filterParams, page, pageSize]);

  const handleClear = () => {
    setSelectedDiscount(null);
    setSearchText('');
    setIsReadOnly(false);
    onChange(null);
    prevValueRef.current = null;
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleFilter({ description: searchText, productId: product?.id });
      setShowModal(true);
    }
  };

  const handleFilter = (filters) => {
    if (JSON.stringify(previousFilterParams.current) !== JSON.stringify(filters)) {
      setFilterParams(filters);
      setPage(1);
      fetchDiscounts(filters, 1, pageSize);
      previousFilterParams.current = filters;
    }
  };

  return (
    <>
      <div className="mb-3">
        {label && <Form.Label className="mb-1">{label}</Form.Label>}
        <InputGroup className={isInvalid ? 'is-invalid' : ''}>
          <FormControl
            placeholder="Selecionar tabela de descontos"
            value={value?.name || searchText}
            isInvalid={isInvalid}
            readOnly={isReadOnly || readOnly}
            onChange={(e) => setSearchText(e.target.value)}
            onKeyDown={handleKeyDown}
            autoComplete="off"
          />
          <Button variant="outline-secondary" onClick={handleClear} disabled={userRoles}>
            <FaTimes />
          </Button>
        </InputGroup>
        {isInvalid && <div className="invalid-feedback">{label} é obrigatória.</div>}
      </div>

      <Modal show={showModal} onHide={() => setShowModal(false)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Selecionar Tabela de Descontos</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {createdNew && (
            <div style={{ textAlign: 'center' }} className="mb-3">
              <Button
                variant="primary"
                className="button-custom mt-0"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowModal(false);
                  setShowCreateModal(true);
                }}
              >
                <FaPlus className="me-2" />
                Novo
              </Button>
            </div>
          )}

          <Table striped bordered hover responsive="sm" className="mt-3">
            <thead>
              <tr>
                <th>Descrição</th>
                <th>Produto</th>
                <th style={{ width: '50px' }}>Ação</th>
              </tr>
            </thead>
            <tbody>
              {discounts.map((discount) => (
                <tr key={discount.id}>
                  <td>{discount.description}</td>
                  <td>{discount.product?.description || '-'}</td>
                  <td>
                    <Button
                      variant="outline-primary"
                      onClick={() => handleSelectDiscount(discount)}
                      className="btn-primary button-custom d-flex align-items-center justify-content-center p-1"
                      style={{ borderRadius: '4px', padding: '5px 10px', minWidth: '40px', height: '30px' }}
                    >
                      <FaCheck style={{ color: 'inherit', opacity: 0.6 }} />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
          <Row className="mt-3">
            <Col>
              <PaginationComponent
                totalPages={totalPages}
                currentPage={page}
                onPageChange={setPage}
                totalElements={totalPages * pageSize}
                size={pageSize}
                pageNumber={page - 1}
                numberOfElements={discounts.length}
              />
            </Col>
          </Row>
        </Modal.Body>
      </Modal>

      <CreateDiscountModal
        show={showCreateModal}
        handleClose={() => setShowCreateModal(false)}
        onCreated={(created) => {
          setShowCreateModal(false);
          handleSelectDiscount(created);
        }}
      />
    </>
  );
};

export default DiscountInput;